import React, { useEffect, useState } from "react";
import { api, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { confirmAction } from "../ConfirmDialog";
import { Loader2, Plus, Trash2, RefreshCw, CalendarClock, Gauge, Pencil, X, CheckCircle2 } from "lucide-react";
import { MAINT_TYPES } from "./VehicleOpsTabs";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const UNITS = [
  { value: "day", label: "jour(s)" },
  { value: "week", label: "semaine(s)" },
  { value: "month", label: "mois" },
  { value: "year", label: "an(s)" },
];
const UNIT_LABELS = Object.fromEntries(UNITS.map((u) => [u.value, u.label]));

const EMPTY_RULE = {
  title: "", type: "entretien",
  calendar_enabled: true, interval_value: "6", interval_unit: "month",
  km_enabled: false, interval_km: "",
  alert_days_before: "30", alert_km_before: "500",
  last_done_date: "", last_done_km: "",
};

function ruleStatus(rule, mileage) {
  const today = new Date().toISOString().slice(0, 10);
  const lead = Number(rule.alert_days_before) || 30;
  const leadKm = Number(rule.alert_km_before) || 500;
  let dateOverdue = false, dateSoon = false, kmOverdue = false, kmSoon = false;
  if (rule.next_due_date) {
    const delta = Math.round((new Date(rule.next_due_date) - new Date(today)) / 86400000);
    dateOverdue = delta < 0;
    dateSoon = delta <= lead;
  }
  if (rule.next_due_km != null) {
    kmOverdue = mileage >= Number(rule.next_due_km);
    kmSoon = mileage >= Number(rule.next_due_km) - leadKm;
  }
  if (dateOverdue || kmOverdue) return { key: "overdue", label: "En retard", cls: "bg-red-100 text-red-700" };
  if (dateSoon || kmSoon) return { key: "soon", label: "À prévoir", cls: "bg-amber-100 text-amber-700" };
  return { key: "ok", label: "À jour", cls: "bg-emerald-100 text-emerald-700" };
}

export function MaintenancePlanSection({ vehicleId }) {
  const [rules, setRules] = useState(null);
  const [mileage, setMileage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_RULE);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(null); // {id, done_date, done_km}

  const load = () =>
    api.get(`/vendor/vehicles/${vehicleId}/maintenance-rules`).then((r) => {
      setRules(r.data.rules || []);
      setMileage(r.data.vehicle_mileage || 0);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vehicleId]);

  const openCreate = () => {
    setForm({ ...EMPTY_RULE, last_done_date: new Date().toISOString().slice(0, 10), last_done_km: String(mileage || "") });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setForm({
      title: rule.title || "", type: rule.type || "entretien",
      calendar_enabled: !!rule.calendar_enabled, interval_value: rule.interval_value != null ? String(rule.interval_value) : "",
      interval_unit: rule.interval_unit || "month",
      km_enabled: !!rule.km_enabled, interval_km: rule.interval_km != null ? String(rule.interval_km) : "",
      alert_days_before: String(rule.alert_days_before ?? 30), alert_km_before: String(rule.alert_km_before ?? 500),
      last_done_date: rule.last_done_date || "", last_done_km: rule.last_done_km != null ? String(rule.last_done_km) : "",
    });
    setEditingId(rule.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.calendar_enabled && !form.km_enabled) {
      toast.error("Activez au moins une récurrence (calendaire ou kilométrique)");
      return;
    }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      type: form.type,
      calendar_enabled: form.calendar_enabled,
      interval_value: form.calendar_enabled && form.interval_value !== "" ? parseInt(form.interval_value, 10) : null,
      interval_unit: form.interval_unit,
      km_enabled: form.km_enabled,
      interval_km: form.km_enabled && form.interval_km !== "" ? parseInt(form.interval_km, 10) : null,
      alert_days_before: form.alert_days_before === "" ? 30 : parseInt(form.alert_days_before, 10),
      alert_km_before: form.alert_km_before === "" ? 500 : parseInt(form.alert_km_before, 10),
      last_done_date: form.last_done_date || null,
      last_done_km: form.last_done_km === "" ? null : parseInt(form.last_done_km, 10),
    };
    try {
      if (editingId) {
        await api.patch(`/vendor/vehicles/${vehicleId}/maintenance-rules/${editingId}`, body);
        toast.success("Plan d'entretien mis à jour");
      } else {
        await api.post(`/vendor/vehicles/${vehicleId}/maintenance-rules`, body);
        toast.success("Plan d'entretien créé");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_RULE);
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const confirmComplete = async () => {
    const c = completing;
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/maintenance-rules/${c.id}/complete`, {
        done_date: c.done_date || null,
        done_km: c.done_km === "" ? null : parseInt(c.done_km, 10),
      });
      toast.success("Entretien enregistré · prochaine échéance reprogrammée");
      setCompleting(null);
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/vendor/vehicles/${vehicleId}/maintenance-rules/${rule.id}`, { active: !rule.active });
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (rule) => {
    if (!(await confirmAction({
      title: "Supprimer ce plan d'entretien ?",
      message: `« ${rule.title} » ne générera plus d'échéance automatique. L'historique des interventions n'est pas affecté.`,
      confirmLabel: "Supprimer le plan",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/vendor/vehicles/${vehicleId}/maintenance-rules/${rule.id}`);
      toast.success("Plan supprimé");
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const recurrenceText = (rule) => {
    const bits = [];
    if (rule.calendar_enabled && rule.interval_value) bits.push(`tous les ${rule.interval_value} ${UNIT_LABELS[rule.interval_unit] || rule.interval_unit}`);
    if (rule.km_enabled && rule.interval_km) bits.push(`tous les ${Number(rule.interval_km).toLocaleString("fr-FR")} km`);
    return bits.join(" ou ");
  };

  if (rules === null) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4" data-testid="maintenance-plan-section">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-violet-50 flex items-center justify-center">
            <RefreshCw className="h-4 w-4 text-violet-600" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">Plan d'entretien récurrent</div>
            <div className="text-[11px] text-slate-500">Échéances calendaires et/ou kilométriques · compteur actuel : {Number(mileage).toLocaleString("fr-FR")} km</div>
          </div>
        </div>
        <button
          onClick={() => (showForm ? (setShowForm(false), setEditingId(null)) : openCreate())}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-maintenance-rule-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Ajouter un entretien récurrent"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-2 gap-3 mb-4" data-testid="maintenance-rule-form">
          <div>
            <label className={labelCls}>Intitulé *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Vidange moteur" className={inputCls + " mt-1"} data-testid="rule-title" />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-type">
              {MAINT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="rule-calendar-toggle">
              <input type="checkbox" checked={form.calendar_enabled} onChange={(e) => setForm({ ...form, calendar_enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" data-testid="rule-calendar-checkbox" />
              <CalendarClock className="h-4 w-4 text-violet-600" />
              <span className="text-xs font-semibold text-slate-800">Récurrence calendaire</span>
            </label>
            {form.calendar_enabled && (
              <div className="flex items-end gap-2 mt-2">
                <div className="w-24">
                  <label className={labelCls}>Tous les</label>
                  <input type="number" min="1" value={form.interval_value} onChange={(e) => setForm({ ...form, interval_value: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-interval-value" />
                </div>
                <select value={form.interval_unit} onChange={(e) => setForm({ ...form, interval_unit: e.target.value })} className={inputCls + " flex-1"} data-testid="rule-interval-unit">
                  {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="rule-km-toggle">
              <input type="checkbox" checked={form.km_enabled} onChange={(e) => setForm({ ...form, km_enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" data-testid="rule-km-checkbox" />
              <Gauge className="h-4 w-4 text-violet-600" />
              <span className="text-xs font-semibold text-slate-800">Récurrence kilométrique</span>
            </label>
            {form.km_enabled && (
              <div className="mt-2 max-w-[220px]">
                <label className={labelCls}>Tous les (km)</label>
                <input type="number" min="1" value={form.interval_km} onChange={(e) => setForm({ ...form, interval_km: e.target.value })} placeholder="10000" className={inputCls + " mt-1"} data-testid="rule-interval-km" />
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Dernier entretien effectué le</label>
            <input type="date" value={form.last_done_date} onChange={(e) => setForm({ ...form, last_done_date: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-last-done-date" />
            <p className="text-[10px] text-slate-400 mt-1">Sert d'ancre pour calculer la prochaine échéance (défaut : aujourd'hui).</p>
          </div>
          <div>
            <label className={labelCls}>Km au dernier entretien</label>
            <input type="number" min="0" value={form.last_done_km} onChange={(e) => setForm({ ...form, last_done_km: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-last-done-km" />
            <p className="text-[10px] text-slate-400 mt-1">Défaut : compteur actuel ({Number(mileage).toLocaleString("fr-FR")} km).</p>
          </div>

          {form.calendar_enabled && (
            <div>
              <label className={labelCls}>Alerte avant échéance (jours)</label>
              <input type="number" min="0" value={form.alert_days_before} onChange={(e) => setForm({ ...form, alert_days_before: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-alert-days" />
            </div>
          )}
          {form.km_enabled && (
            <div>
              <label className={labelCls}>Alerte avant échéance (km)</label>
              <input type="number" min="0" value={form.alert_km_before} onChange={(e) => setForm({ ...form, alert_km_before: e.target.value })} className={inputCls + " mt-1"} data-testid="rule-alert-km" />
            </div>
          )}

          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="rule-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {editingId ? "Mettre à jour" : "Enregistrer le plan"}
            </button>
          </div>
        </form>
      )}

      {rules.length === 0 ? (
        !showForm && (
          <div className="py-8 text-center rounded-2xl border border-dashed border-slate-200" data-testid="maintenance-rules-empty">
            <RefreshCw className="h-7 w-7 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">Aucun entretien récurrent configuré.</div>
            <div className="text-[11px] text-slate-400 mt-1">Programmez vidanges, contrôles techniques, révisions… une seule fois.</div>
          </div>
        )
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => {
            const status = ruleStatus(rule, mileage);
            const inactive = rule.active === false;
            return (
              <div key={rule.id} className={`rounded-xl border bg-white p-3.5 ${inactive ? "border-slate-100 opacity-60" : "border-slate-200"}`} data-testid={`maintenance-rule-row-${rule.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{rule.title}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">{MAINT_TYPES.find((t) => t.value === rule.type)?.label || rule.type}</span>
                      {!inactive && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${status.cls}`} data-testid={`rule-status-badge-${rule.id}`}>{status.label}</span>}
                      {inactive && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">En pause</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">Récurrence : {recurrenceText(rule) || "—"}</div>
                    <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`rule-next-due-${rule.id}`}>
                      {rule.next_due_date && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3 text-violet-500" /> Prochaine : {formatDateFR(rule.next_due_date)}</span>}
                      {rule.next_due_km != null && <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3 text-violet-500" /> À {Number(rule.next_due_km).toLocaleString("fr-FR")} km</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setCompleting({ id: rule.id, done_date: new Date().toISOString().slice(0, 10), done_km: String(mileage || "") })} className="h-7 px-2 rounded-md hover:bg-emerald-50 text-emerald-600 inline-flex items-center gap-1 text-[10px] font-semibold" title="Marquer comme fait" data-testid={`rule-complete-${rule.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Fait
                    </button>
                    <button onClick={() => toggleActive(rule)} className="h-7 px-2 rounded-md hover:bg-slate-100 text-slate-500 text-[10px] font-semibold" data-testid={`rule-toggle-active-${rule.id}`}>
                      {inactive ? "Reprendre" : "Pause"}
                    </button>
                    <button onClick={() => openEdit(rule)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" title="Modifier" data-testid={`rule-edit-${rule.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(rule)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`rule-delete-${rule.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {completing?.id === rule.id && (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 flex flex-wrap items-end gap-3" data-testid={`rule-complete-form-${rule.id}`}>
                    <div>
                      <label className={labelCls}>Effectué le</label>
                      <input type="date" value={completing.done_date} onChange={(e) => setCompleting({ ...completing, done_date: e.target.value })} className={inputCls + " mt-1"} data-testid={`rule-done-date-${rule.id}`} />
                    </div>
                    <div>
                      <label className={labelCls}>Km au compteur</label>
                      <input type="number" min="0" value={completing.done_km} onChange={(e) => setCompleting({ ...completing, done_km: e.target.value })} className={inputCls + " mt-1"} data-testid={`rule-done-km-${rule.id}`} />
                    </div>
                    <button onClick={confirmComplete} className="h-[38px] px-4 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700" data-testid={`rule-done-confirm-${rule.id}`}>Reprogrammer</button>
                    <button onClick={() => setCompleting(null)} className="h-[38px] px-3 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50">Annuler</button>
                    <p className="text-[10px] text-emerald-700 w-full">La prochaine échéance sera recalculée à partir de cette date / de ce kilométrage.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
